/* eslint-disable complexity */
import { lazy, Suspense, useEffect } from "react";

import { useUFOComponentExperience } from "@atlassian/ufo";

import { FeatureFlags } from "@/components/signup/bxpkit-signup/src/model";
import { LayoutType } from "@/components/signup/bxpkit-signup/src/sign-up-layout-container";
import { ufoEventPaidSignupLandingPageLoad } from "@/components/signup/utils";
import TryIntent from "@/components/try-intent/src";
import {
    useConfluenceIntent,
    useDevAiIntent,
    useLoomIntent,
    useJiraIntent,
    useJiraServiceManagementIntent,
    useJiraProductDiscoveryIntent,
    useProductIntent,
    useServiceCollectionIntent,
    useTeamworkCollectionIntent,
    useUserContext
} from "@/components/user-context";
import {
    AA_AUTOCOMPLETE_POTENTIAL_CONVERSION_EVENT_LOCAL_STORAGE_KEY,
    sendConversionEventAlgolia
} from "@/patterns/global-nav/src/auto-complete-helpers";
import {
    defaultFeatureFlags,
    defaultLabels,
    defaultProductData,
    getQueryParams,
    firstImpressionsImageUrls,
    useLabelsLocalized
} from "@/templates/try/cloud/signup/signup-page/src/utils";
import { getLocalStorage } from "@/utils/localstorage";

import { ClientOnly, useLocation } from "../../../../../../utils/";

import { ErrorLocale } from "./components";
import { ConfluenceSignup } from "./confluence/confluence-signup";
import { DevAiSignup } from "./dev-ai/dev-ai-signup";
import { JiraSignup } from "./jira/jira-signup";
import { JpdSignup } from "./jira-product-discovery/jpd-signup";
import { JsmSignup } from "./jira-service-management/jsm-signup";
import { LoomSignup } from "./loom/loom-signup";
import { ServiceCollectionSignup } from "./service-collection/service-collection-signup";
import { TeamworkCollectionSignup } from "./teamwork-collection/teamwork-collection-signup";
import { handleSignupRouteRedirects } from "./utils/signup-redirection-resolver";

const SignUpLayoutContainer = lazy(
    () =>
        // @ts-ignore
        import(
            "../../../../../../components/signup/bxpkit-signup/src/sign-up-layout-container.tsx"
        )
);

type SignupPageTemplateProps = {
    layout?: LayoutType;
    featureFlags?: FeatureFlags;
};
export type SignupPageTemplateBaseProps = {
    bundle?: string;
    migrationSourceUuid?: string;
    edition?: string;
    layout?: LayoutType;
    featureFlags?: FeatureFlags;
};

export const SignupPageTemplate = ({
    featureFlags
}: SignupPageTemplateProps) => {
    const location = useLocation();
    const { bundle: useProductIntentBundle } = useProductIntent(location.href);

    const { product, bundle, edition, migrationSourceUuid, layout } =
        getQueryParams(location.search);

    if (handleSignupRouteRedirects({ product: product as string, bundle })) {
        return <></>;
    }

    return (
        <SignupPageTemplateBase
            bundle={useProductIntentBundle}
            edition={edition}
            layout={layout}
            featureFlags={{
                ...defaultFeatureFlags,
                ...featureFlags
            }}
            migrationSourceUuid={migrationSourceUuid}
        />
    );
};

export const SignupPageTemplateBase = ({
    bundle,
    edition,
    layout,
    featureFlags,
    migrationSourceUuid
}: SignupPageTemplateBaseProps) => {
    useUFOComponentExperience(ufoEventPaidSignupLandingPageLoad);

    return (
        <Suspense>
            <div>
                <SignUpComponent
                    bundleOverride={bundle}
                    editionOverride={edition}
                    layout={layout}
                    featureFlags={{
                        ...defaultFeatureFlags,
                        ...featureFlags
                    }}
                    migrationSourceUuid={migrationSourceUuid}
                />
            </div>
        </Suspense>
    );
};

// TODO: Temporary fix for hot-120109
const SignUpComponent = ({
    bundleOverride,
    editionOverride,
    layout = "first-impressions",
    featureFlags,
    migrationSourceUuid
}: {
    bundleOverride?: string;
    editionOverride?: string;
    migrationSourceUuid?: string;
    layout?: LayoutType;
    isN2NParam?: boolean;
    featureFlags: FeatureFlags;
}) => {
    const { user: userProfile } = useUserContext();
    const isJSW = useJiraIntent();
    const isJSM = useJiraServiceManagementIntent();
    const isJPD = useJiraProductDiscoveryIntent();
    const isDevAI = useDevAiIntent();
    const isLoom = useLoomIntent();
    const isConfluence = useConfluenceIntent();
    const isServiceCollection = useServiceCollectionIntent();
    const isTeamworkCollection = useTeamworkCollectionIntent();

    const countryCode =
        typeof window !== "undefined" ? window.countryCode : undefined;
    const blockedLocales = ["RU", "BY"];
    const labels = useLabelsLocalized(defaultLabels);

    useEffect(() => {
        const getaaCompleteQueryObj = getLocalStorage(
            AA_AUTOCOMPLETE_POTENTIAL_CONVERSION_EVENT_LOCAL_STORAGE_KEY
        );
        if (getaaCompleteQueryObj) {
            const parsedStringifyAttributes = JSON.parse(getaaCompleteQueryObj);
            sendConversionEventAlgolia({
                objectID: parsedStringifyAttributes.objectID,
                queryID: parsedStringifyAttributes.queryID,
                position: parsedStringifyAttributes.position
            });
        }
    }, []);

    if (countryCode && blockedLocales.includes(countryCode)) {
        return <ErrorLocale locale={countryCode} />;
    }

    if (userProfile.requestInProgress) {
        return <></>;
    }

    if (layout !== "standalone" && !migrationSourceUuid) {
        if (isJSW) {
            return <JiraSignup />;
        }

        if (isJSM) {
            return <JsmSignup />;
        }

        if (isJPD) {
            return <JpdSignup />;
        }

        if (isDevAI) {
            return <DevAiSignup />;
        }

        if (isConfluence) {
            return <ConfluenceSignup />;
        }

        if (isLoom) {
            return <LoomSignup />;
        }

        if (isServiceCollection) {
            return <ServiceCollectionSignup />;
        }

        if (isTeamworkCollection) {
            return <TeamworkCollectionSignup />;
        }
    }

    return (
        <ClientOnly>
            <TryIntent
                intentType="form"
                productKey={bundleOverride}
                edition={editionOverride}
            >
                <SignUpLayoutContainer
                    layout={layout}
                    allowSignupWithGoogle={true}
                    allowSignupWithAllSocialOptions={true}
                    bundleOverride={bundleOverride}
                    editionOverride={editionOverride}
                    productData={defaultProductData}
                    enableFreeBreachTest={true}
                    labels={labels}
                    firstImpressionsImageUrls={firstImpressionsImageUrls}
                    featureFlags={featureFlags}
                />
            </TryIntent>
        </ClientOnly>
    );
};
